import React from 'react';
import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Divider,
  Stack,
} from '@mui/material';
import { VolumeUp, Equalizer, Compress } from '@mui/icons-material';

interface LoudnessMetrics {
  integratedLUFS: number;
  peakDb: number;
  truePeakDb: number;
  rmsDb: number;
  crestFactor: number;
  dynamicRange: number;
}

interface LoudnessMetersProps {
  target: LoudnessMetrics;
  reference: LoudnessMetrics;
  mastered: LoudnessMetrics;
}

// Streaming platform targets
const PLATFORM_TARGETS = {
  spotify: { lufs: -14, label: 'Spotify', color: '#1DB954' },
  appleMusic: { lufs: -16, label: 'Apple Music', color: '#FC3C44' },
  youtube: { lufs: -13, label: 'YouTube', color: '#FF0000' },
  tidal: { lufs: -14, label: 'Tidal', color: '#000000' },
};

const MetricBar: React.FC<{
  label: string;
  value: number;
  min: number;
  max: number;
  target?: number;
  unit: string;
  color?: string;
}> = ({ label, value, min, max, target, unit, color = 'primary' }) => {
  const percentage = ((value - min) / (max - min)) * 100;
  const targetPercentage = target ? ((target - min) / (max - min)) * 100 : null;

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="caption" fontWeight="bold">
          {value.toFixed(1)} {unit}
        </Typography>
      </Box>
      <Box sx={{ position: 'relative' }}>
        <LinearProgress
          variant="determinate"
          value={Math.max(0, Math.min(100, percentage))}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              bgcolor: color,
            },
          }}
        />
        {targetPercentage !== null && (
          <Box
            sx={{
              position: 'absolute',
              left: `${targetPercentage}%`,
              top: -2,
              width: 2,
              height: 12,
              bgcolor: 'warning.main',
              transform: 'translateX(-50%)',
            }}
          />
        )}
      </Box>
    </Box>
  );
};

const LoudnessMeters: React.FC<LoudnessMetersProps> = ({ target, reference, mastered }) => {
  // Determine which platform target is closest
  const getClosestPlatform = (lufs: number) => {
    let closest = { ...PLATFORM_TARGETS.spotify, diff: Math.abs(lufs - PLATFORM_TARGETS.spotify.lufs) };
    Object.values(PLATFORM_TARGETS).forEach((platform) => {
      const diff = Math.abs(lufs - platform.lufs);
      if (diff < closest.diff) {
        closest = { ...platform, diff };
      }
    });
    return closest;
  };

  const closestPlatform = getClosestPlatform(mastered.integratedLUFS);
  const isOnTarget = Math.abs(mastered.integratedLUFS - closestPlatform.lufs) < 1.0;

  return (
    <Paper elevation={2} sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <VolumeUp color="primary" />
        <Typography variant="h6">Loudness Analysis</Typography>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
        gap: 2
      }}>
        {/* Target */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Original Target
          </Typography>
          <MetricBar
            label="Integrated LUFS"
            value={target.integratedLUFS}
            min={-40}
            max={0}
            unit="LUFS"
            color="info.main"
          />
          <MetricBar
            label="Peak"
            value={target.peakDb}
            min={-20}
            max={0}
            unit="dBFS"
            color="info.main"
          />
          <MetricBar
            label="Dynamic Range"
            value={target.dynamicRange}
            min={0}
            max={20}
            unit="LU"
            color="info.main"
          />
        </Paper>

        {/* Reference */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Reference
          </Typography>
          <MetricBar
            label="Integrated LUFS"
            value={reference.integratedLUFS}
            min={-40}
            max={0}
            unit="LUFS"
            color="secondary.main"
          />
          <MetricBar
            label="Peak"
            value={reference.peakDb}
            min={-20}
            max={0}
            unit="dBFS"
            color="secondary.main"
          />
          <MetricBar
            label="Dynamic Range"
            value={reference.dynamicRange}
            min={0}
            max={20}
            unit="LU"
            color="secondary.main"
          />
        </Paper>

        {/* Mastered */}
        <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Mastered Output
          </Typography>
          <MetricBar
            label="Integrated LUFS"
            value={mastered.integratedLUFS}
            min={-40}
            max={0}
            target={closestPlatform.lufs}
            unit="LUFS"
            color="success.dark"
          />
          <MetricBar
            label="Peak"
            value={mastered.peakDb}
            min={-20}
            max={0}
            target={-1}
            unit="dBFS"
            color="success.dark"
          />
          <MetricBar
            label="Dynamic Range"
            value={mastered.dynamicRange}
            min={0}
            max={20}
            unit="LU"
            color="success.dark"
          />
        </Paper>
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Streaming Platform Targets */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Equalizer fontSize="small" />
          Streaming Platform Compatibility
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
          {Object.values(PLATFORM_TARGETS).map((platform) => {
            const diff = Math.abs(mastered.integratedLUFS - platform.lufs);
            const compatible = diff < 1.5;
            return (
              <Chip
                key={platform.label}
                label={`${platform.label}: ${platform.lufs} LUFS`}
                size="small"
                color={compatible ? 'success' : 'default'}
                variant={compatible ? 'filled' : 'outlined'}
                sx={{ mb: 0.5 }}
              />
            );
          })}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          {isOnTarget
            ? `✓ Mastered output is optimized for ${closestPlatform.label}`
            : `Closest to ${closestPlatform.label} target (${closestPlatform.diff.toFixed(1)} LU difference)`}
        </Typography>
      </Box>

      {/* Additional Metrics */}
      <Box>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Compress fontSize="small" />
          Technical Details
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
          mt: 0.5
        }}>
          <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              RMS Level
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {mastered.rmsDb.toFixed(1)} dB
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Crest Factor
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {mastered.crestFactor.toFixed(1)} dB
            </Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary" display="block">
              True Peak
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {mastered.truePeakDb.toFixed(1)} dBTP
            </Typography>
          </Paper>
        </Box>
      </Box>
    </Paper>
  );
};

export default LoudnessMeters;
